// Deliberate duplicate of town-square/src/components/arty/actions/ArtyActionsProvider.tsx (ADR 0002). A change here belongs there too.
// Client divergences: `sendText` may return a promise (the flyout's send is async), and the
// toast host is the `SonnerToaster` client already mounts.
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { toast } from 'sonner';

import { usePinnedActions, type PinnedActionsState } from '~/hooks/usePinnedActions';
import { DEFAULT_PINS, MAX_PINS } from '~/lib/arty-actions/pins';
import { livePins, type ArtyAction } from '~/lib/arty-actions/catalog';

export type ArtySendOptions = {
  /** The CompletionType id the tile binds to; the spark's framing rides the turn, not the sentence. */
  sparkId?: string;
  /** The piece the tile was tapped on, so two cards never send the same turn. */
  imageId?: string;
  imageUrl?: string;
  imageTitle?: string;
};

export type ArtyChatActions = {
  isBusy: boolean;
  isSignedIn: boolean;
  sendText: (text: string, options?: ArtySendOptions) => void | Promise<void>;
};

export type ToastKind = 'success' | 'error';

/** Every card toast through the one `SonnerToaster` client already mounts. */
export function notify(message: string, kind?: ToastKind): void {
  if (kind === 'success') {
    toast.success(message);
    return;
  }
  if (kind === 'error') {
    toast.error(message);
    return;
  }
  toast(message);
}

function noop() {
  return undefined;
}

const PINS_FALLBACK: PinnedActionsState = {
  pins: [...DEFAULT_PINS],
  isCustom: false,
  atCap: DEFAULT_PINS.length >= MAX_PINS,
  toggle: noop,
  reset: noop,
};

const CHAT_FALLBACK: ArtyChatActions = { isBusy: false, isSignedIn: false, sendText: noop };

const PinsContext = createContext<PinnedActionsState>(PINS_FALLBACK);
const ChatActionsContext = createContext<ArtyChatActions>(CHAT_FALLBACK);

/** Shortcuts, shared so every card and the sheet never disagree. */
export function usePins(): PinnedActionsState {
  return useContext(PinsContext);
}

/**
 * The sheet's view of the pins: only those that still point at one of this piece's actions. A
 * pin to a spark the account lost would otherwise count toward the cap with no tile to unstar.
 * Until the spark library has loaded (`sparksReady`) a pin with no tile yet is kept, so a star
 * tap while it loads, or after it failed, cannot delete the artist's library pins.
 */
export function LivePinsProvider({
  actions,
  sparksReady,
  children,
}: {
  actions: readonly ArtyAction[];
  sparksReady: boolean;
  children: ReactNode;
}) {
  const base = usePins();
  const value = useMemo(() => {
    const live = livePins(actions, base.pins, sparksReady);
    return {
      ...base,
      pins: live,
      atCap: live.length >= MAX_PINS,
      toggle: (id: string) => base.toggle(id, live),
    };
  }, [actions, base, sparksReady]);
  return <PinsContext.Provider value={value}>{children}</PinsContext.Provider>;
}

/** The host chat's send capability, for anything drawn inside the transcript. */
export function useArtyChatActions(): ArtyChatActions {
  return useContext(ChatActionsContext);
}

/**
 * Mounted once around the transcript so every card shares the same shortcuts
 * and the same way back into the chat. `chat.sendText` opens a new user turn;
 * the host decides how the turn is sent and scrolled.
 */
export function ArtyActionsProvider({ chat, children }: { chat: ArtyChatActions; children: ReactNode }) {
  const { isBusy, isSignedIn, sendText } = chat;
  const pins = usePinnedActions(isSignedIn);
  const chatValue = useMemo(() => {
    return { isBusy, isSignedIn, sendText };
  }, [isBusy, isSignedIn, sendText]);

  return (
    <PinsContext.Provider value={pins}>
      <ChatActionsContext.Provider value={chatValue}>{children}</ChatActionsContext.Provider>
    </PinsContext.Provider>
  );
}
